import { useEffect, useState } from "react";
import { publicSiteConfigAPI, PublicSiteConfig } from "../services/api";

// Module-level cache so the flag is fetched once per page load regardless of
// how many components consult it (Navbar, route guards, ...). A browser
// refresh re-fetches, which is exactly the "appears on next refresh"
// behavior the marketplace toggle promises (issue #1400).
let cachedConfig: PublicSiteConfig | null = null;
let pendingFetch: Promise<PublicSiteConfig> | null = null;

const fetchConfig = (): Promise<PublicSiteConfig> => {
  if (cachedConfig) return Promise.resolve(cachedConfig);
  if (!pendingFetch) {
    pendingFetch = publicSiteConfigAPI
      .getConfig()
      .then((res) => {
        cachedConfig = res.data;
        return res.data;
      })
      .catch(() => {
        // Fail closed: if the config cannot be loaded, gated features stay
        // hidden. Not cached so a later consumer can retry.
        pendingFetch = null;
        return { marketplace_enabled: false };
      });
  }
  return pendingFetch;
};

export interface UseSiteConfigResult {
  loading: boolean;
  marketplaceEnabled: boolean;
}

export const useSiteConfig = (): UseSiteConfigResult => {
  const [config, setConfig] = useState<PublicSiteConfig | null>(cachedConfig);

  useEffect(() => {
    let mounted = true;
    if (!config) {
      fetchConfig().then((c) => {
        if (mounted) setConfig(c);
      });
    }
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    loading: config === null,
    marketplaceEnabled: config?.marketplace_enabled ?? false,
  };
};
